// ============================================================
// PettAI (பேட்டை) — CREDIT CONTEXT (Firebase Cloud Functions)
// Wired to Cloud Functions via httpsCallable
// Location: src/contexts/CreditContext.js
// ============================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from './AuthContext';
import {
    PLANS,
    getCreditStatus,
    canGenerate as canGenerateCheck,
    formatPrice,
} from '../config/pricing';

const CreditContext = createContext(null);
const functions = getFunctions();

export const useCredits = () => {
    const context = useContext(CreditContext);
    if (!context) {
        return {
            creditsRemaining: 0,
            creditsUsed: 0,
            totalCredits: 0,
            creditStatus: getCreditStatus(0, 'starter'),
            loading: true,
            plan: PLANS.starter,
            planId: 'starter',
            planName: 'Starter',
            cycleStartDate: new Date(),
            cycleEndDate: new Date(),
            daysRemaining: 30,
            dailyBurnRate: 0,
            projectedExhaustionDate: null,
            usageLog: [],
            addTopUpCredits: async () => ({ success: false, error: 'Not initialized' }),
            canGenerate: () => false,
            getCreditCost: () => 1,
            formatPrice,
        };
    }
    return context;
};

export const CreditProvider = ({ children }) => {
    const { currentUser: user } = useAuth();
    const [creditsUsed, setCreditsUsed] = useState(0);
    const [creditsAdded, setCreditsAdded] = useState(0);
    const [usageLog, setUsageLog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [planId, setPlanId] = useState('starter');

    const plan = PLANS[planId];
    const totalCredits = plan.monthlyCredits === Infinity
        ? Infinity
        : plan.monthlyCredits + creditsAdded;
    const creditsRemaining = totalCredits === Infinity
        ? Infinity
        : Math.max(0, totalCredits - creditsUsed);
    const creditStatus = getCreditStatus(creditsUsed, planId);

    // Cycle dates
    const now = new Date();
    const cycleStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const cycleEndDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const daysRemaining = Math.max(0, Math.ceil(
        (cycleEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    ));
    const daysElapsed = Math.max(1, Math.ceil(
        (now.getTime() - cycleStartDate.getTime()) / (1000 * 60 * 60 * 24)
    ));
    const dailyBurnRate = creditsUsed > 0 ? Math.round(creditsUsed / daysElapsed) : 0;

    let projectedExhaustionDate = null;
    if (dailyBurnRate > 0 && creditsRemaining !== Infinity) {
        const d = new Date();
        d.setDate(d.getDate() + Math.ceil(creditsRemaining / dailyBurnRate));
        projectedExhaustionDate = d;
    }

    // Load user credits from Firestore via Cloud Function
    useEffect(() => {
        if (!user) {
            setLoading(false);
            return;
        }
        const loadCredits = async () => {
            try {
                const getUserCredits = httpsCallable(functions, 'getUserCredits');
                const result = await getUserCredits();
                const data = result.data;
                setCreditsUsed(data.used || 0);
                setCreditsAdded(0);
                setPlanId(data.plan || 'starter');
                setUsageLog([]);
            } catch (error) {
                console.error('Error loading credits:', error);
            } finally {
                setLoading(false);
            }
        };
        loadCredits();
    }, [user]);

    // Top-up credits: USER-FACING FLOW DEFERRED to F-003 Part 2 (Razorpay).
    // The Cloud Function 'addTopUpCredits' is now admin-only (Batch 4 Part 1).
    // When Razorpay is wired, this will call createRazorpayOrder ->
    // verifyRazorpayPayment instead of the admin endpoint.
    const addTopUpCredits = useCallback(async (packId) => {
        return {
            success: false,
            error: 'top_up_not_available',
            message: 'Credit top-up will be available once Razorpay integration is complete.',
        };
    }, []);

    const value = {
        creditsRemaining,
        creditsUsed,
        totalCredits,
        creditStatus,
        loading,
        plan,
        planId,
        planName: plan.name,
        cycleStartDate,
        cycleEndDate,
        daysRemaining,
        dailyBurnRate,
        projectedExhaustionDate,
        usageLog,
        addTopUpCredits,
        canGenerate: (contentType) => canGenerateCheck(creditsRemaining, contentType, planId),
        getCreditCost: (contentType) => plan.creditCosts[contentType] || 0,
        formatPrice,
    };

    return (
        <CreditContext.Provider value={value}>
            {children}
        </CreditContext.Provider>
    );
};

export default CreditContext;